import type { Account, OperatorOption } from '../api/monitor';

export type AccountFieldName = 'username' | 'password' | 'otpEmail' | 'otpAppPassword';

export interface AccountField {
  name: AccountFieldName;
  placeholder: string;
  type: 'text' | 'password' | 'email';
  inputMode?: 'numeric' | 'email';
  /** Token de app: monoespaçado, porque é para conferir caractere a caractere. */
  mono?: boolean;
}

/**
 * Campos do cadastro por operadora.
 *
 * O que muda entre Claro e Vivo não é preferência de tela: é como cada portal
 * autentica. A Claro GOL loga com o CPF do titular e manda um OTP por e-mail,
 * que o eagle lê por IMAP — daí a caixa e o token de app. A Vivo entra direto
 * com o id da conta e a senha.
 *
 * Operadora que não estiver aqui cai no formulário genérico (login + senha) em
 * vez de sumir do cadastro: `operators` é tabela, e pode crescer sem passar por
 * este arquivo.
 */
const FIELDS_BY_OPERATOR: Record<string, AccountField[]> = {
  claro: [
    {
      name: 'username',
      placeholder: 'CPF do titular (000.000.000-00)',
      type: 'text',
      inputMode: 'numeric',
    },
    { name: 'password', placeholder: 'Senha do portal Claro', type: 'password' },
    { name: 'otpEmail', placeholder: 'E-mail Gmail (recebe o OTP)', type: 'email' },
    { name: 'otpAppPassword', placeholder: 'Token de app do Google', type: 'text', mono: true },
  ],
  vivo: [
    { name: 'username', placeholder: 'ID da conta (número)', type: 'text', inputMode: 'numeric' },
    { name: 'password', placeholder: 'Senha do portal Vivo', type: 'password' },
  ],
};

export function accountFields(operator: OperatorOption): AccountField[] {
  return (
    FIELDS_BY_OPERATOR[operator.code] ?? [
      { name: 'username', placeholder: `Login no portal ${operator.name}`, type: 'text' },
      { name: 'password', placeholder: `Senha do portal ${operator.name}`, type: 'password' },
    ]
  );
}

/** Operadora que autentica com OTP — quem manda o aviso do Gmail para a tela. */
export function usesOtp(operator: OperatorOption): boolean {
  return accountFields(operator).some((field) => field.name === 'otpEmail');
}

/** `459.073.638-19`; o banco guarda só dígitos. */
export function formatCpf(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 11) return value;

  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

/** Iniciais da operadora no quadradinho colorido da lista (CL, VI). */
export function operatorShort(operator: OperatorOption): string {
  return operator.name.slice(0, 2).toUpperCase();
}

/** Primeira linha da conta: o login, lido do jeito que o portal o chama. */
export function accountTitle(account: Account): string {
  return account.operator.code === 'claro'
    ? `CPF ${formatCpf(account.username)}`
    : `Conta ${account.username}`;
}

/**
 * Segunda linha: dono e credenciais, sempre mascaradas.
 *
 * O master vem da sincronização com o SGC, então conta recém-cadastrada ainda
 * não tem um — e dizer isso é mais útil do que deixar a linha vazia.
 */
export function accountSubtitle(account: Account): string {
  const parts = [account.master ?? 'Sem master vinculado'];

  if (account.otpEmail) parts.push(account.otpEmail);
  if (account.hasOtpAppPassword) parts.push('token de app ••••');
  else if (!account.otpEmail) parts.push('senha ••••••');

  return parts.join(' · ');
}

/** `12 linhas` / `1 linha`, com as inativas explicadas quando existem. */
export function linesLabel(lines: Account['lines']): string {
  const label = lines.total === 1 ? '1 linha' : `${lines.total.toLocaleString('pt-BR')} linhas`;

  return lines.total > lines.active ? `${label} (${lines.active} ativas)` : label;
}

/** Minúsculas e sem acento, para a busca achar "Goiania" em "Goiânia". */
function fold(text: string): string {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

/** True quando a conta casa com a busca da tela de Contas (login, dono, e-mail). */
export function accountMatches(account: Account, needle: string): boolean {
  const term = fold(needle.trim());
  if (!term) return true;

  const haystack = fold(
    [account.username, account.master ?? '', account.company ?? '', account.otpEmail ?? ''].join(' ')
  );

  return haystack.includes(term);
}
